// What a click is called, from strings only (the DOM stays in click.ts): the
// name the sweep gave the control (`data-track`, with its optional
// `data-track-value`), and a label reduced to its fixed part so nothing
// identifying leaves the browser.

/** The strings a control offers for its label; any may be missing. */
export interface LabelSource {
  ariaLabel?: string | null;
  title?: string | null;
  text?: string | null;
  /** A `data-track` on the control itself: a stable name, sent as it is. */
  dataTrack?: string | null;
}

/** The longest label sent. */
export const MAX_LABEL = 40;

/**
 * The control's label: `data-track` when present; else the aria-label, else
 * the title, else the text, each reduced by `reduceLabel`.
 */
export function labelFrom(source: LabelSource): string {
  const named = clean(source.dataTrack);
  if (named) return named.slice(0, MAX_LABEL);
  for (const raw of [source.ariaLabel, source.title, source.text]) {
    const label = reduceLabel(clean(raw));
    if (label) return label;
  }
  return '';
}

/**
 * A label's fixed part. The app's labels put what varies after a separator
 * (`Oct 19 · Lost · vs caio1973 1635 · not reviewed yet`, `Main line: the
 * game's book moves…`), name the opponent after `vs`, or title a game by its
 * two players; each is cut, redacted or replaced:
 * - cut at the first ` · ` or `:`;
 * - two names around an em dash (the game bar's title) become `game`;
 * - ratings in parentheses are dropped, then the label is cut at the first ` (`;
 * - `vs <name>` becomes `vs …`;
 * - whitespace collapsed, 40 characters at most.
 */
export function reduceLabel(raw: string): string {
  let label = clean(raw);
  const cut = label.search(/ · |:/);
  if (cut >= 0) label = label.slice(0, cut);
  if (/\S — \S/.test(label)) return 'game';
  label = label.replace(/\s*\(\d{3,4}\)/g, '');
  const paren = label.indexOf(' (');
  if (paren >= 0) label = label.slice(0, paren);
  label = label.replace(/\bvs \S+/g, 'vs …');
  return clean(label).slice(0, MAX_LABEL).trim();
}

/** One element on the way up from the click's target: its tracking attributes, when it has them. */
export interface TrackAttributes {
  name?: string | null;
  value?: string | null;
}

/** The name and value a click resolves to: the nearest `data-track` wins, with the nearest `data-track-value` at or below it. */
export function resolveTrack(chain: readonly TrackAttributes[]): { name: string; value?: string } | undefined {
  const at = chain.findIndex((el) => clean(el.name));
  if (at < 0) return undefined;
  const name = clean(chain[at].name);
  const value = chain
    .slice(0, at + 1)
    .map((el) => clean(el.value))
    .find(Boolean);
  return value ? { name, value } : { name };
}

function clean(s: string | null | undefined): string {
  return (s ?? '').replace(/\s+/g, ' ').trim();
}
